import type { ReactNode } from "react";
import { LaundryMark } from "../LaundryMark";

/**
 * Empty states are part of the product, not a fallback.
 *
 * Copy convention: the title is a terse noun phrase with no full stop
 * ("No orders yet"); the description is one sentence pointing at the next
 * action. Errors use "Couldn't …", never "Failed to" or "Error:".
 *
 * The icon passed in is always `size-6`.
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  watermark = false,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  /** A faint brand mark behind the content. Used sparingly — hero empties only. */
  watermark?: boolean;
}) {
  return (
    <div className="relative flex flex-col items-center justify-center gap-3 overflow-hidden px-6 py-14 text-center">
      {watermark && (
        <LaundryMark
          size={140}
          className="pointer-events-none absolute -top-6 start-1/2 -translate-x-1/2 opacity-[0.04]"
        />
      )}
      {icon && (
        <div className="relative flex size-14 items-center justify-center rounded-2xl bg-ink-50 text-muted-subtle ring-1 ring-inset ring-line/70">
          {icon}
        </div>
      )}
      <div className="relative">
        <p className="text-[0.9375rem] font-semibold tracking-tight text-ink-800">{title}</p>
        {description && (
          <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
      {action && <div className="relative mt-1">{action}</div>}
    </div>
  );
}
